import { Koi } from '@ksyos/koi';
import type { TypedReadSchema } from '@common/formEngine/typedReadSchema.types';
import type { TypedSchema } from '@common/validation/utils/typedSchemaType';
import { MdoInterimReportAdvice } from '@common/customControls/bariatrics';
import { getTextAreaStringSchema } from '@common/validation/utils/getTextAreaStringSchema';
import type { AdviesAan } from './../../utils';

export type BariPreMdoInterimReportData = AdviesAan & {
    datum_mdo: string | null;
    inclusie_in_zorgpad: MdoInterimReportAdvice | null;
    klaar_voor_versturen: boolean;
    toelichting: string | null;
    verslag_bespreking_mdo: string | null;
    verwijsbrief_nodig: boolean | null;
};

export type BariPreMdoInterimReportReadKey = keyof BariPreMdoInterimReportData;
export type BariPreMdoInterimReportReadLabel =
    | 'Advies aan patiënt'
    | 'Advies aan huisarts'
    | 'Advies aan chirurg'
    | 'Conclusie'
    | 'Datum MDO'
    | 'Toelichting'
    | 'Verslag bespreking MDO'
    | 'Verwijsbrief nodig';
export type BariPreMdoInterimReportSectionTitle = 'Conclusie';

export const bariPreMdoInterimReportReadSchema: TypedReadSchema<
    BariPreMdoInterimReportReadKey,
    BariPreMdoInterimReportReadLabel,
    BariPreMdoInterimReportSectionTitle
>[] = [
    {
        items: [
            { key: 'datum_mdo', label: 'Datum MDO', type: 'dateAsString' },
            { key: 'verslag_bespreking_mdo', label: 'Verslag bespreking MDO' },
        ],
    },
    {
        items: [
            { key: 'inclusie_in_zorgpad', label: 'Conclusie' },
            { key: 'toelichting', label: 'Toelichting' },
            { key: 'verwijsbrief_nodig', label: 'Verwijsbrief nodig', type: 'boolean' },
            { key: 'advies_aan_patient', label: 'Advies aan patiënt' },
            { key: 'advies_aan_huisarts', label: 'Advies aan huisarts' },
            { key: 'advies_aan_chirurg', label: 'Advies aan chirurg' },
        ],
        sectionTitle: 'Conclusie',
    },
];

const bariPreMdoInterimReportFinal: TypedSchema<BariPreMdoInterimReportData> = {
    advies_aan_chirurg: getTextAreaStringSchema(true),
    advies_aan_huisarts: getTextAreaStringSchema(true),
    advies_aan_patient: getTextAreaStringSchema(true),
    datum_mdo: Koi.koi().date(),
    inclusie_in_zorgpad: Koi.string().valid(...Object.values(MdoInterimReportAdvice)),
    klaar_voor_versturen: Koi.boolean().valid(true),
    toelichting: getTextAreaStringSchema(),
    verslag_bespreking_mdo: getTextAreaStringSchema(),
    verwijsbrief_nodig: Koi.when('inclusie_in_zorgpad', {
        is: [MdoInterimReportAdvice.continueWithGuidance, MdoInterimReportAdvice.notYet],
        otherwise: Koi.valid(null),
        then: Koi.boolean(),
    }),
};

// klaar_voor_versturen was deliberately left out because we don't want that value to be saved in the draft
const bariPreMdoInterimReportDraft: TypedSchema<Omit<BariPreMdoInterimReportData, 'klaar_voor_versturen'>> = {
    advies_aan_chirurg: getTextAreaStringSchema(true),
    advies_aan_huisarts: getTextAreaStringSchema(true),
    advies_aan_patient: getTextAreaStringSchema(true),
    datum_mdo: Koi.koi().date().allow(null),
    inclusie_in_zorgpad: Koi.string()
        .valid(...Object.values(MdoInterimReportAdvice))
        .allow(null),
    toelichting: getTextAreaStringSchema(true),
    verslag_bespreking_mdo: getTextAreaStringSchema(true),
    verwijsbrief_nodig: Koi.boolean().allow(null),
};

export const bariPreMdoInterimReportDraftSchema = Koi.object(bariPreMdoInterimReportDraft);
export const bariPreMdoInterimReportFinalSchema = Koi.object(bariPreMdoInterimReportFinal);
